import React, { useEffect, useState } from 'react';
import apiService from '../services/api';

interface CaseEvent {
  id: string;
  actorName?: string | null;
  actorRole?: string | null;
  eventType: string;
  visibility: 'public' | 'internal';
  title: string;
  description?: string | null;
  metadata?: Record<string, unknown>;
  occurredAt: string;
}

interface CaseTimelineProps {
  reportId: string;
  reportNumber?: string;
  onClose: () => void;
}

const CaseTimeline: React.FC<CaseTimelineProps> = ({ reportId, reportNumber, onClose }) => {
  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    apiService.getReportTimeline(reportId)
      .then((response) => {
        if (!active) return;
        setEvents(response.success && response.data ? response.data : []);
      })
      .catch(() => {
        if (active) setError('The case history could not be loaded.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reportId]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50">
      <div className="mx-auto my-10 w-full max-w-3xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Case history</h2>
            {reportNumber && <p className="text-sm text-gray-500 dark:text-gray-400">Report {reportNumber}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded px-3 py-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100">
            Close
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto px-6 py-5">
          {isLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading case history…</p>}
          {error && <p className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {!isLoading && !error && events.length === 0 && (
            <p className="rounded bg-gray-50 p-4 text-sm text-gray-600 dark:text-gray-400">
              No structured events have been recorded yet. New status and technician updates will appear here.
            </p>
          )}
          <ol className="relative ml-3 border-l border-gray-200">
            {events.map((event) => (
              <li key={event.id} className="mb-7 ml-6">
                <span className="absolute -left-2.5 mt-1 h-5 w-5 rounded-full border-4 border-white bg-blue-600" />
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">{event.title}</h3>
                  <span className={`rounded-full px-2 py-1 text-xs ${
                    event.visibility === 'public'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {event.visibility}
                  </span>
                </div>
                {event.description && <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{event.description}</p>}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {new Date(event.occurredAt).toLocaleString()}
                  {event.actorName ? ` · ${event.actorName}` : ''}
                  {event.actorRole ? ` (${event.actorRole})` : ''}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default CaseTimeline;
